import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  CircularProgress,
  Button,
  Grid,
  TextField,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControlLabel,
  Checkbox,
  Radio,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { CheckOutApi, OrderViewApi, ProfileView, ProfileEditApi } from "../../Utility/UtilApi/UtilApi";
import UtilToastMessages from "../../Utility/UtilToastMessages/UtilToastMessages";
import { buttonColor, buttonHoverColor, primaryColor } from "../../UtilColorPalate/UtilColorPalate";

const CheckoutPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const orderIdFromCart = location.state?.orderId;

  const [orderData, setOrderData] = useState(null);
  const [currencyCode, setCurrencyCode] = useState("$");
  const [profileData, setProfileData] = useState(null);
  const [billingAddress, setBillingAddress] = useState({});
  // Initialize shippingAddress with empty values
  const [shippingAddress, setShippingAddress] = useState({
    addresslineone: "",
    addresslinetwo: "",
    city: "",
    province: "",
    country: "",
    postalcode: "",
  });
  const [useBillingForShipping, setUseBillingForShipping] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isSnackbarOpen, setSnackbarOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastColor, setToastColor] = useState("");
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedShippingAddressId, setSelectedShippingAddressId] = useState("");
  const [addressMode, setAddressMode] = useState("new");

  const getTodayDate = () => {
    const today = new Date();
    const options = { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: true };
    return today.toLocaleString("en-US", options).replace(",", "");
  };

  const fetchOrderDetails = async () => {
    try {
      setLoading(true);
      const response = await OrderViewApi();
      if (response.success && response.data && response.data.orders) {
        const order = response.data.orders.find((o) => o.type === "Order" && o.recordid === orderIdFromCart) || 
                      response.data.orders.find((o) => o.type === "Order");
        setOrderData(order || null);
        setCurrencyCode(response.data.currencycode || "$");
      }
    } catch (error) {
      setToastMessage(error.message || "Failed to load checkout details.");
      setToastColor("error");
      setSnackbarOpen(true);
    }
  };

  const fetchProfile = async () => {
    try {
      const response = await ProfileView();
      const profile = response.data.profile;
      const addresses = response.data.addresses || [];

      const billingAddr = addresses.find(addr => addr.type?.name === "Billing") || {};
      setBillingAddress({
        addresslineone: billingAddr.addresslineone || "",
        addresslinetwo: billingAddr.addresslinetwo || "",
        city: billingAddr.city || "",
        province: billingAddr.province || "",
        country: billingAddr.country || "",
        postalcode: billingAddr.postalcode || "",
      });

      // Don't set shippingAddress here - keep it empty initially
      setSelectedShippingAddressId(""); // Reset selection
      setProfileData(profile);
      setSavedAddresses(addresses);
    } catch (error) {
      setToastMessage("Failed to load profile details.");
      setToastColor("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      // Reset shipping address on every navigation
      setShippingAddress({
        addresslineone: "",
        addresslinetwo: "",
        city: "",
        province: "",
        country: "",
        postalcode: "",
      });
      setSelectedShippingAddressId("");
      setUseBillingForShipping(false);
      setAddressMode("new");

      await Promise.all([fetchOrderDetails(), fetchProfile()]);
      setLoading(false);
    };
    loadData();
  }, [location]); // Add location as dependency to trigger on navigation

  const resetShippingInputs = () => {
    setShippingAddress({
      addresslineone: "",
      addresslinetwo: "",
      city: "",
      province: "",
      country: "",
      postalcode: "",
    });
    setSelectedShippingAddressId("");
    setUseBillingForShipping(false);
    setAddressMode("new");
  };

  const handleShippingAddressChange = (field, value) => {
    if (!useBillingForShipping) {
      setShippingAddress((prev) => ({ ...prev, [field]: value }));
      if (addressMode === "existing") {
        setSelectedShippingAddressId(""); // Clear selection if manually editing
      }
    }
  };

  const handleSelectAddress = (e) => {
    const selectedId = e.target.value;
    setSelectedShippingAddressId(selectedId);
    if (!useBillingForShipping) {
      if (selectedId === "") {
        setShippingAddress({ addresslineone: "", addresslinetwo: "", city: "", province: "", country: "", postalcode: "" });
      } else {
        const selected = savedAddresses.find((addr) => addr.id === selectedId);
        setShippingAddress({
          addresslineone: selected.addresslineone || "",
          addresslinetwo: selected.addresslinetwo || "",
          city: selected.city || "",
          province: selected.province || "",
          country: selected.country || "",
          postalcode: selected.postalcode || "",
        });
      }
    }
  };

  const handleUseBillingForShippingChange = (e) => {
    const checked = e.target.checked;
    setUseBillingForShipping(checked);
    if (checked) {
      setShippingAddress({ ...billingAddress });
      setSelectedShippingAddressId("");
      setAddressMode("existing");
    } else {
      setShippingAddress({
        addresslineone: "",
        addresslinetwo: "",
        city: "",
        province: "",
        country: "",
        postalcode: "",
      });
      setSelectedShippingAddressId("");
      setAddressMode("new");
    }
  };

  const validateShippingAddress = () => {
    const requiredFields = ["addresslineone", "city", "province", "country", "postalcode"];
    const errors = [];

    if (!useBillingForShipping && addressMode === "new") {
      requiredFields.forEach(field => {
        if (!shippingAddress[field]?.trim()) {
          errors.push(`${field === "addresslineone" ? "Address Line 1" : 
                       field === "postalcode" ? "Postal Code" : 
                       field.charAt(0).toUpperCase() + field.slice(1)} is required`);
        }
      });
    }
    return errors;
  };

  const handlePayNow = async () => {
    if (!orderData || !orderData.recordid) {
      setToastMessage("No order available to process payment.");
      setToastColor("error");
      setSnackbarOpen(true);
      return;
    }

    const validationErrors = validateShippingAddress();
    if (validationErrors.length > 0) {
      setToastMessage(validationErrors.join(", "));
      setToastColor("error");
      setSnackbarOpen(true);
      return;
    }

    try {
      let shippingAddressPayload = {};

      if (useBillingForShipping) {
        shippingAddressPayload = {
          name: shippingAddress.addresslineone,
          type: "Shipping",
          addresslineone: billingAddress.addresslineone || "",
          addresslinetwo: billingAddress.addresslinetwo || "",
          city: billingAddress.city || "",
          province: billingAddress.province || "",
          country: billingAddress.country || "",
          postalcode: billingAddress.postalcode || "",
        };
      } else if (selectedShippingAddressId && addressMode === "existing") {
        const selectedAddr = savedAddresses.find(addr => addr.id === selectedShippingAddressId);
        shippingAddressPayload = {
          recordid: selectedAddr.recordid,
          type: "Shipping",
          addresslineone: selectedAddr.addresslineone || "",
          addresslinetwo: selectedAddr.addresslinetwo || "",
          city: selectedAddr.city || "",
          province: selectedAddr.province || "",
          country: selectedAddr.country || "",
          postalcode: selectedAddr.postalcode || "",
        };
      } else {
        shippingAddressPayload = {
          name: shippingAddress.addresslineone,
          type: "Shipping",
          addresslineone: shippingAddress.addresslineone || "",
          addresslinetwo: shippingAddress.addresslinetwo || "",
          city: shippingAddress.city || "",
          province: shippingAddress.province || "",
          country: shippingAddress.country || "",
          postalcode: shippingAddress.postalcode || "",
        };
      }

      const profileEditPayload = {
        profile: {
          firstname: profileData.firstname || "",
          lastname: profileData.lastname || "",
          emailoptout: Boolean(profileData.emailoptout),
          smsoptout: Boolean(profileData.smsoptout),
          calloptout: Boolean(profileData.calloptout),
          consentprovided: Boolean(profileData.consentprovided),
          name: `${profileData.firstname || ""} ${profileData.lastname || ""}`.trim(),
        },
        addresses: [shippingAddressPayload],
      };
// console.log("profileEditPayload",profileEditPayload);


      // const profileEditResponse = await ProfileEditApi(profileEditPayload);
      // if (!profileEditResponse.success) throw new Error(profileEditResponse.message || "Failed to save shipping address");

      // setToastMessage("Shipping address saved successfully!");
      // setToastColor("success");
      setSnackbarOpen(true);

      const checkoutPayload = {
        order: orderData.recordid,
      };

      const checkoutResponse = await CheckOutApi(checkoutPayload);
      if (!checkoutResponse.success) throw new Error(checkoutResponse.message || "Checkout failed");

      setToastMessage(checkoutResponse.message || "Payment processing initiated!");
      setToastColor("success");
      setSnackbarOpen(true);

      resetShippingInputs();

      if (checkoutResponse.data.redirecturl) {
        window.location.href = checkoutResponse.data.redirecturl;
      } else {
        navigate("/shop");
      }
    } catch (error) {
      setToastMessage(error.message || "Failed to process payment.");
      setToastColor("error");
      setSnackbarOpen(true);
      resetShippingInputs();
    }
  };

  const formatPrice = (value) => `${currencyCode}${value.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const renderBillingAddress = () => (
    <Box sx={{ mb: 1 }}>
      <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: "600", color: primaryColor }}>
        Billing Address
      </Typography>
      <Grid container spacing={1}>
        {["addresslineone", "addresslinetwo", "city", "province", "country", "postalcode"].map((field) => (
          <Grid item xs={12} sm={6} key={field}>
            <TextField
              label={field === "addresslineone" ? "Address Line 1" : 
                     field === "addresslinetwo" ? "Address Line 2" : 
                     field === "postalcode" ? "Postal Code" : field.charAt(0).toUpperCase() + field.slice(1)}
              value={billingAddress[field] || "--"}
              fullWidth
              variant="outlined"
              size="small"
              InputProps={{ readOnly: true }}
              sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", backgroundColor: "#f5f5f5" } }} 
            />
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderShippingAddress = () => {
    const shippingAddresses = savedAddresses.filter(addr => addr.type?.name === "Shipping");

    const handleAddressModeChange = (mode) => {
      setAddressMode(mode);
      if (mode === "new" && !useBillingForShipping) {
        setShippingAddress({
          addresslineone: "",
          addresslinetwo: "",
          city: "",
          province: "",
          country: "",
          postalcode: "",
        });
        setSelectedShippingAddressId("");
      }
    };

    return (
      <Box sx={{ mb: 1 }}>
        <FormControlLabel
          control={<Checkbox checked={useBillingForShipping} onChange={handleUseBillingForShippingChange} style={{ color: primaryColor }} />}
          label="Use billing address for shipping"
          sx={{ mt: 1 }}
        />
        <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: "600", color: primaryColor }}>
          Shipping Address (Required)
        </Typography>

        {!useBillingForShipping && (
          <>
            <Box sx={{ mb: 2 }}>

              {shippingAddresses.length !== 0 && (

<FormControlLabel
control={
  <Radio
    checked={addressMode === "existing"}
    onChange={() => handleAddressModeChange("existing")}
    disabled={shippingAddresses.length === 0}
    sx={{ color: primaryColor, '&.Mui-checked': { color: primaryColor } }}
  />
}
label="Select an existing address"
/>
              ) }
             
              <FormControlLabel
                control={
                  <Radio
                    checked={addressMode === "new"}
                    onChange={() => handleAddressModeChange("new")}
                    sx={{ color: primaryColor, '&.Mui-checked': { color: primaryColor } }}
                  />
                }
                label="Create new address"
              />
            </Box>

            {addressMode === "existing" && (
              <Select
                value={selectedShippingAddressId || ""}
                onChange={handleSelectAddress}
                displayEmpty
                fullWidth
                sx={{ mb: 2, borderRadius: "6px" }}
              >
                <MenuItem value="" disabled>
                  {shippingAddresses.length === 0 ? "No addresses available" : "Select an address"}
                </MenuItem>
                {shippingAddresses.map((addr) => (
                  <MenuItem key={addr.id} value={addr.id}>{`${addr.addresslineone}, ${addr.city}`}</MenuItem>
                ))}
              </Select>
            )}
          </>
        )}

        <Grid container spacing={1}>
          {["addresslineone", "addresslinetwo", "city", "province", "country", "postalcode"].map((field) => (
            <Grid item xs={12} sm={6} key={field}>
              <TextField
                label={field === "addresslineone" ? "Address Line 1" : 
                       field === "addresslinetwo" ? "Address Line 2" : 
                       field === "postalcode" ? "Postal Code" : field.charAt(0).toUpperCase() + field.slice(1)}
                value={useBillingForShipping ? (billingAddress[field] || "--") : (shippingAddress[field] || "")}
                onChange={(e) => handleShippingAddressChange(field, e.target.value)}
                fullWidth
                variant="outlined"
                size="small"
                required={field !== "addresslinetwo"}
                disabled={useBillingForShipping || (addressMode === "existing" && selectedShippingAddressId !== "" && !useBillingForShipping)}
                sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px" } }}
                // error={field !== "addresslinetwo" && !useBillingForShipping && addressMode === "new" && !shippingAddress[field]?.trim()}
                helperText={field !== "addresslinetwo" && !useBillingForShipping && addressMode === "new" && !shippingAddress[field]?.trim() }
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <Container sx={{ py: 2 }}>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
          <CircularProgress size={30} />
        </Box>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 2, borderRadius: "8px", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}>
              <Typography variant="h6" sx={{ fontWeight: "700", mb: 1, color: "#333" }}>
                Checkout Details
              </Typography>

              {profileData && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: "600" }}>
                    Personal Information
                  </Typography>
                  <Grid container spacing={1}>
                    {["firstname", "lastname", "email", "mobile"].map((field) => (
                      <Grid item xs={12} sm={6} key={field}>
                        <TextField
                          label={field.charAt(0).toUpperCase() + field.slice(1)}
                          value={profileData[field] || "--"}
                          fullWidth
                          variant="outlined"
                          size="small"
                          InputProps={{ readOnly: true }}
                          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "6px", backgroundColor: "#f5f5f5" } }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {renderBillingAddress()}
              {renderShippingAddress()}
            </Paper>
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper 
              sx={{ 
                p: 2, 
                borderRadius: "12px", 
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)"
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  fontWeight: "700", 
                  mb: 2, 
                  color: "#2c3e50",
                  background: "linear-gradient(to right, #2c3e50, #3498db)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent"
                }}
              >
                Cart Summary
              </Typography>
              
              {orderData ? (
                <Box sx={{ borderRadius: "8px", overflow: "hidden" }}>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: "#f1f3f5" }}>
                          <TableCell sx={{ py: 1, color: "#2c3e50", fontWeight: "600" }}>Item</TableCell>
                          <TableCell align="right" sx={{ py: 1, color: "#2c3e50", fontWeight: "600" }}>Price</TableCell>
                          <TableCell align="right" sx={{ py: 1, color: "#2c3e50", fontWeight: "600" }}>Qty</TableCell>
                          <TableCell align="right" sx={{ py: 1, color: "#2c3e50", fontWeight: "600" }}>Total</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {(orderData.deallineitems || []).map((item, index) => (
                          <TableRow 
                            key={item.id}
                            sx={{
                              backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                              "&:hover": { backgroundColor: "#f5f6ff" },
                              transition: "background-color 0.2s"
                            }}
                          >
                            <TableCell sx={{ py: 1, color: "#444" }}>{item.name || "Unnamed Item"}</TableCell>
                            <TableCell align="right" sx={{ py: 1 }}>{formatPrice(item.lineitemtotal / item.quantity)}</TableCell>
                            <TableCell align="right" sx={{ py: 1 }}>{item.quantity || 0}</TableCell>
                            <TableCell align="right" sx={{ py: 1, fontWeight: "500", color: "#2c3e50" }}>
                              {formatPrice(item.lineitemtotal)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <Box sx={{ mt: 2, textAlign: "right" }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        fontWeight: "700", 
                        color: "#27ae60",
                        mb: 1
                      }}
                    >
                      Total: {formatPrice(orderData.dealamount)}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: "#7f8c8d",
                        display: "block",
                        mb: 2
                      }}
                    >
                      Date: {getTodayDate()}
                    </Typography>
                    
                    <Button
                      variant="contained"
                      onClick={handlePayNow}
                      disabled={!orderData || !profileData}
                      fullWidth
                      sx={{
                        backgroundColor: buttonColor,
                        color: "#fff",
                        textTransform: "none",
                        padding: "5px 15px",
                        borderRadius: "4px",
                        "&:hover": { backgroundColor: buttonHoverColor },
                      }}
                    >
                      Pay Now
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Typography 
                  sx={{ 
                    color: "#e74c3c", 
                    textAlign: "center",
                    py: 2,
                    fontWeight: "500"
                  }}
                >
                  No order found.
                </Typography>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      <UtilToastMessages
        isOpen={isSnackbarOpen}
        handleClose={() => setSnackbarOpen(false)}
        data={toastMessage}
        severity={toastColor}
      />
    </Container>
  );
};

export default CheckoutPage;